import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FolderKanban,
  Library,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  LogOut,
  User,
  LayoutDashboard,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSidebar } from "@/contexts/SidebarContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTheme } from "@/hooks/useTheme";
import { cn } from "@/lib/utils";
import { signOut } from "@/integrations/firebase/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface SidebarProps {
  isAdmin?: boolean;
}

interface NavItem {
  path: string;
  icon: any;
  label: string;
  badge?: string;
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

export const Sidebar = ({ isAdmin }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { collapsed, setCollapsed } = useSidebar();
  const isMobile = useIsMobile();
  const { isDark } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoHovered, setLogoHovered] = useState(false);

  // Get company/org name and logo from profile
  const profileData = profile as any;
  // Use orgName first (from orgs collection), then fall back to companyName, then displayName
  const companyName = profile?.orgName || profileData?.companyName || profileData?.company_name || profile?.displayName || "My Company";
  const companyLogo = profileData?.companyLogo || profileData?.company_logo;
  const fullName = profileData?.fullName || profileData?.full_name || profile?.email || "User";

  // Theme-aware logo paths
  const logoPath = "/icons/normiz-favicon.svg";
  const userRole = isAdmin ? "Admin" : "User";

  // Get first letter for avatar
  const companyInitial = companyName.charAt(0).toUpperCase();

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  // Navigation items - Consolidated TurboTax-style IA
  // Removed: Compliance (demoted to permit detail), merged Evidence Vault → Library
  // Removed: Developments (merged into My Work page)
  const getNavSections = (): NavSection[] => {
    const sections: NavSection[] = [
      {
        // Primary: Daily workflow (Reports merged into My Work, Developments merged into My Work)
        items: [
          { path: "/permits", icon: LayoutDashboard, label: "My Work" },
          { path: "/jobs", icon: FolderKanban, label: "Permits" },
          { path: "/documents", icon: Library, label: "Library" },
        ]
      },
      {
        // System
        items: [
          { path: "/profile", icon: Settings, label: "Settings" },
        ]
      }
    ];

    return sections;
  };

  const navSections = getNavSections();

  // Company avatar component for dropdown
  const CompanyAvatar = () => {
    if (companyLogo) {
      return (
        <img
          src={companyLogo}
          alt={companyName}
          className="h-5 w-5 rounded object-cover flex-shrink-0"
        />
      );
    }

    return (
      <div className="h-5 w-5 rounded bg-sidebar-primary flex items-center justify-center text-xs font-bold text-sidebar-primary-foreground flex-shrink-0">
        {companyInitial}
      </div>
    );
  };

  // Render nav items
  const renderNavItem = (item: NavItem, onClick?: () => void) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;

    return (
      <Link
        to={item.path}
        onClick={onClick}
        className={cn(
          "flex items-center gap-2.5 py-2 rounded-lg transition-colors",
          isActive
            ? "bg-sidebar-primary text-sidebar-primary-foreground"
            : "text-sidebar-foreground hover:bg-sidebar-accent",
          collapsed ? "px-2 justify-center" : "px-2.5"
        )}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        {!collapsed && (
          <span className="font-medium text-sm whitespace-nowrap flex-1">{item.label}</span>
        )}
        {!collapsed && item.badge && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-sidebar-accent text-sidebar-foreground/70">
            {item.badge}
          </span>
        )}
      </Link>
    );
  };

  // Render section
  const renderSection = (section: NavSection, index: number) => {
    return (
      <div key={index} className={index > 0 ? "mt-2" : ""}>
        {section.title && !collapsed && (
          <div className="px-2.5 py-2 text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider">
            {section.title}
          </div>
        )}
        <ul className="space-y-0.5">
          {section.items.map((item) => (
            <li key={item.path}>
              {collapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {renderNavItem(item)}
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-popover text-popover-foreground">
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              ) : (
                renderNavItem(item)
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Mobile hamburger menu
  if (isMobile) {
    return (
      <>
        {/* Mobile menu button */}
        <button
          onClick={() => setMobileOpen(true)}
          className="fixed top-4 left-4 z-50 p-2 rounded-lg bg-sidebar text-sidebar-foreground shadow-lg"
          style={{ top: 'max(1rem, env(safe-area-inset-top))' }}
        >
          <Menu className="h-6 w-6" />
        </button>

        {/* Mobile sidebar overlay */}
        {mobileOpen && (
          <div className="fixed inset-0 z-50">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setMobileOpen(false)}
            />

            {/* Sidebar */}
            <aside
              className="absolute left-0 top-0 bottom-0 w-64 bg-sidebar text-sidebar-foreground shadow-xl flex flex-col"
              style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
              {/* Header - Normiz branding */}
              <div className="flex items-center justify-between px-4 h-14">
                <div className="flex items-center gap-2">
                  <img src={logoPath} alt="Normiz" className="h-7 w-7" />
                  <span className="font-bold text-sidebar-foreground">Normiz</span>
                </div>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-2 rounded-lg hover:bg-sidebar-accent text-sidebar-foreground transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Company selector */}
              <div className="px-3 pb-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 w-full p-2 rounded-lg hover:bg-sidebar-accent transition-colors text-left">
                      <CompanyAvatar />
                      <span className="flex-1 text-sm font-medium text-sidebar-foreground truncate">{companyName}</span>
                      <ChevronDown className="h-4 w-4 text-sidebar-foreground/60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuLabel>Your Companies</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>
                      <CompanyAvatar />
                      <span className="ml-2">{companyName}</span>
                    </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Navigation */}
              <nav className="flex-1 overflow-y-auto px-2">
                {navSections.map((section, index) => (
                  <div key={index} className={index > 0 ? "mt-4" : ""}>
                    {section.title && (
                      <div className="px-2.5 py-2 text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider">
                        {section.title}
                      </div>
                    )}
                    <ul className="space-y-0.5">
                      {section.items.map((item) => (
                        <li key={item.path}>
                          {renderNavItem(item, () => setMobileOpen(false))}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </nav>

              {/* User section at bottom */}
              <div className="p-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 w-full p-2 rounded-lg hover:bg-sidebar-accent transition-colors text-left">
                      <div className="h-7 w-7 rounded-full bg-sidebar-accent flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-sidebar-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-sidebar-foreground truncate">{fullName}</p>
                        <p className="text-xs text-sidebar-foreground/60">{userRole}</p>
                      </div>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuLabel>
                      <div className="flex flex-col">
                        <span className="font-medium">{fullName}</span>
                        <span className="text-xs text-muted-foreground">{userRole}</span>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => { setMobileOpen(false); navigate("/profile"); }}>
                      <Settings className="h-4 w-4 mr-2" />
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </aside>
          </div>
        )}
      </>
    );
  }

  // Desktop sidebar - collapsed width is 56px (w-14), expanded is 192px (w-48)
  return (
    <aside
      className={cn(
        "fixed left-0 top-0 bottom-0 z-50 bg-sidebar text-sidebar-foreground border-r border-sidebar-border transition-all duration-200 flex flex-col",
        collapsed ? "w-14" : "w-48"
      )}
    >
      {/* Header - Normiz branding with hover-to-expand when collapsed */}
      <div className={cn(
        "flex items-center h-14",
        collapsed ? "justify-center px-2" : "justify-between px-3"
      )}>
        {collapsed ? (
          // Collapsed: Show logo, arrow only on hover
          <button
            onMouseEnter={() => setLogoHovered(true)}
            onMouseLeave={() => setLogoHovered(false)}
            onClick={() => {
              setLogoHovered(false);
              setCollapsed(false);
            }}
            className="p-1.5 rounded-lg hover:bg-sidebar-accent transition-colors"
            title="Expand sidebar"
          >
            {logoHovered ? (
              <ChevronRight className="h-6 w-6 text-sidebar-foreground" />
            ) : (
              <img src={logoPath} alt="Normiz" className="h-6 w-6" />
            )}
          </button>
        ) : (
          // Expanded: Show logo + name + collapse button
          <>
            <div className="flex items-center gap-2">
              <img src={logoPath} alt="Normiz" className="h-7 w-7 flex-shrink-0" />
              <span className="font-bold whitespace-nowrap text-sidebar-foreground">Normiz</span>
            </div>
            <button
              onClick={() => {
                setLogoHovered(false);
                setCollapsed(true);
              }}
              className="p-1 rounded hover:bg-sidebar-accent text-sidebar-foreground transition-colors flex-shrink-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      {/* Company selector - only when expanded */}
      {!collapsed && (
        <div className="px-2 pb-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 w-full p-2 rounded-lg hover:bg-sidebar-accent transition-colors text-left">
                <CompanyAvatar />
                <span className="flex-1 text-sm font-medium text-sidebar-foreground truncate">{companyName}</span>
                <ChevronDown className="h-4 w-4 text-sidebar-foreground/60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel>Your Companies</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <CompanyAvatar />
                <span className="ml-2">{companyName}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Navigation - no divider, flows directly from company selector */}
      <nav className="flex-1 overflow-y-auto px-1.5">
        <TooltipProvider delayDuration={0}>
          {navSections.map((section, index) => renderSection(section, index))}
        </TooltipProvider>
      </nav>

      {/* User account section at bottom - only when expanded */}
      {!collapsed && (
        <div className="p-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 w-full p-2 rounded-lg hover:bg-sidebar-accent transition-colors text-left">
                <div className="h-7 w-7 rounded-full bg-sidebar-accent flex items-center justify-center flex-shrink-0">
                  <User className="h-4 w-4 text-sidebar-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-sidebar-foreground truncate">{fullName}</p>
                  <p className="text-xs text-sidebar-foreground/60">{userRole}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="font-medium">{fullName}</span>
                  <span className="text-xs text-muted-foreground">{userRole}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/profile")}>
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </aside>
  );
};
